import { FormikErrors, useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { globalOption } from '../../../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCountry';
import { allLanguagesOption } from '../../../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteLanguage';
import { GoogleAdsRequestTypeGq } from '../../../../../../graphql/projects/models/GoogleAdsRequestTypeGq';
import { ProjectTypeGq } from '../../../../../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../../../../../graphql/users/models/CurrentUserGq';
import { KeywordSearchLimits } from '../../../../../../logic/otherUtilities/constants/seedInputLimits';
import { useUserDefaults } from '../../../../../../logic/otherUtilities/useUserDefaults';
import { CreateQuickSearchFormState, validLongTailLanguages } from './QuickSearchModels';

export function setupQuickSearchForm(input: {
  setRequestId: (requestId: number, projectId: number) => void;
  user: CurrentUserGq;
}) {
  const { setRequestId, user } = input;

  // Initial search form values (default)
  const userDefaults = useUserDefaults(user);
  const localStorageRequestType = useMemo(
    () =>
      (localStorage.getItem('quick_request_type') as GoogleAdsRequestTypeGq) ??
      GoogleAdsRequestTypeGq.EXACT,
    []
  );

  // Initial values (default)
  let initialValues: CreateQuickSearchFormState = {
    requestType: localStorageRequestType,
    requestName: '',
    language: userDefaults.volumeLanguage,
    country: userDefaults.volumeCountry,
    city: userDefaults.volumeCity,
    includeZeroVolume: false,
    includeMonths: false,
    source: '',
    categories: [],
    suggestionsWord: false,
    suggestionsAlphabeticals: false,
    suggestionsNumbers: false,
    suggestionsComparisons: false,
    suggestionsPrepositions: false,
    suggestionsQuestions: false,
    maxCharacters: 50000,
  };

  // Mutations
  const createProjectMutation = projectsApiService.mutations.projects.useCreateProject();
  const createAdsRequestMutation =
    projectsApiService.mutations.googleAds.useCreateGoogleAdsRequest();

  const formBag = useFormik<CreateQuickSearchFormState>({
    enableReinitialize: true, // In case a new request is clicked, we'll swap out the values
    initialValues: initialValues,
    validate: (values) => {
      const errors: FormikErrors<CreateQuickSearchFormState> = {};
      const seedCount = values.source?.trim()?.split(/[\n,]+/g)?.length;
      // Seed limits ------------
      if (
        values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL &&
        seedCount > KeywordSearchLimits.LONG_TAIL
      ) {
        errors.source = `More than ${KeywordSearchLimits.LONG_TAIL} autocomplete idea seeds in one search is not allowed.`;
      }
      if (
        values.requestType === GoogleAdsRequestTypeGq.RELATED &&
        seedCount > KeywordSearchLimits.RELATED
      ) {
        errors.source = `More than ${KeywordSearchLimits.RELATED} keyword idea seeds in one search is not allowed`;
      }
      // Remove error message since new UI is limited to one domain idea seed
      /* if (
        values.requestType === GoogleAdsRequestTypeGq.SITE &&
        seedCount > KeywordSearchLimits.SITE
      ) {
        errors.source = `Over ${KeywordSearchLimits.SITE} site seeds are not allowed in one request. Are you sure this is not an url search?`;
      } */
      if (
        values.requestType === GoogleAdsRequestTypeGq.URL &&
        seedCount > KeywordSearchLimits.URL
      ) {
        errors.source = `More than ${KeywordSearchLimits.URL} url idea seeds in one search is not allowed.`;
      }
      if (
        values.requestType === GoogleAdsRequestTypeGq.EXACT &&
        seedCount > KeywordSearchLimits.EXACT
      ) {
        errors.source = `More than ${KeywordSearchLimits.EXACT} exact keyword seeds in one search is not allowed.`;
      }

      // Other ----------------
      if (seedCount === 0 || !(values.source.length > 0) || values.source === '') {
        errors.source = 'This field is required and cannot be empty';
      }
      if (values.source.length >= values.maxCharacters) {
        errors.maxCharacters = 'More than 50 000 characters is not allowed';
      }

      const validLongtailLanguage =
        !values.language?.name || validLongTailLanguages.includes(values.language?.name);

      const anyLongTailSuggestions = !!(
        values.suggestionsWord ||
        values.suggestionsAlphabeticals ||
        values.suggestionsNumbers ||
        (values.suggestionsComparisons && validLongtailLanguage) ||
        (values.suggestionsQuestions && validLongtailLanguage) ||
        (values.suggestionsPrepositions && validLongtailLanguage)
      );

      if (values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL && !anyLongTailSuggestions) {
        errors.suggestionsWord = 'At least 1 type of suggestion is needed';
      }

      return errors;
    },
    onSubmit: (values, actions) =>
      createProjectMutation({ type: ProjectTypeGq.QUICK_SEARCH }).then((projectId) => {
        const validLongtailLanguage =
          !values.language?.name || validLongTailLanguages.includes(values.language?.name);

        // Google ads code
        const seeds = values.source
          ?.split(/[\n,]+/g)
          .filter((v) => !!v?.length)
          .map((v) => v?.trim());
        const name = seeds.slice(0, 4).join(', ').toLowerCase();
        return createAdsRequestMutation({
          projectId: projectId,
          type: values.requestType,
          seeds: seeds,
          name: name.slice(0, 20)?.concat(name?.length > 20 ? '...' : ''),
          locationId: !!values.city?.id
            ? values.city?.id
            : values.country?.id !== globalOption.id
            ? values.country?.id
            : null,
          locationName: !!values.city?.id
            ? values.city?.name
            : values.country?.id !== globalOption.id
            ? values.country?.name
            : null,
          languageId: values.language?.id !== allLanguagesOption.id ? values.language?.id : null,
          languageName:
            values.language?.id !== allLanguagesOption.id ? values.language?.name : null,
          minSearchVolume: values.includeZeroVolume ? 0 : 1,
          includeRankData: false,
          suggestionsWord: values.suggestionsWord,
          suggestionsAlphabeticals: values.suggestionsAlphabeticals,
          suggestionsNumbers: values.suggestionsNumbers,
          suggestionsComparisons: validLongtailLanguage && values.suggestionsComparisons,
          suggestionsQuestions: validLongtailLanguage && values.suggestionsQuestions,
          suggestionsPrepositions: validLongtailLanguage && values.suggestionsPrepositions,
        }).then((requestId) => {
          setRequestId(requestId, projectId);
        });
      }),
  });

  // Update quick search type
  useEffect(() => {
    !!formBag.values.requestType?.length &&
      localStorage.setItem('quick_request_type', formBag.values.requestType);
  }, [formBag.values.requestType]);

  return formBag;
}
